import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lumfi_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({
  baseURL: API,
});

// iter-169zzp — Detect when we're running as an installed PWA so the
// backend can issue a 90-day refresh token instead of the default 7-day
// one. `display-mode: standalone` covers Chrome/Edge/Android; iOS
// Safari uses the legacy `navigator.standalone` boolean. The check
// runs once on module load — installed PWAs don't switch to a tab
// inside the same session.
function _isPwaInstalled() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }
  } catch (_e) {
    /* matchMedia not available in some old browsers */
  }
  // iOS Safari
  return Boolean(window.navigator && window.navigator.standalone);
}
const _IS_PWA = _isPwaInstalled();

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Stamp every request so the backend knows when we're running as
  // an installed PWA (only really matters on /auth/login and
  // /auth/refresh, but it's cheap to include everywhere).
  if (_IS_PWA) config.headers["X-Lumfi-Client"] = "pwa";
  // iter-170am — Stamp the active workspace so backend endpoints can
  // scope queries by space when supported. Read straight from
  // localStorage (single source of truth: SpaceContext writes it) so
  // we don't need to import the React context here.
  try {
    const sid = localStorage.getItem("lumfi_active_space");
    if (sid) config.headers["X-Space-Id"] = sid;
  } catch { /* private mode */ }
  return config;
});

// Global handler for 402/429 credit-exhaustion errors. Surfaces a single
// rich toast with a CTA to /pricing instead of a generic axios error
// bubbling through every UI surface that calls the API.
//
// The toast is fired AT MOST ONCE every 30s so a chat that fires multiple
// requests in a row doesn't carpet-bomb the user.
let _lastCreditToastAt = 0;
let _lastUpsellEmitAt = 0;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // ── 402 "feature_not_entitled" → global upsell modal ─────────────
    // The backend's require_feature() helper returns this dict shape.
    // We rate-limit to 1 emission per 1.2s so a noisy page (e.g.
    // dashboard auto-refreshing 3 gated endpoints) doesn't flash the
    // modal repeatedly. The modal itself is mounted once in App.jsx
    // and subscribes via onUpsellEvent.
    if (status === 402 && detail && typeof detail === "object" && detail.error === "feature_not_entitled") {
      if (Date.now() - _lastUpsellEmitAt > 1200) {
        _lastUpsellEmitAt = Date.now();
        import("../contexts/UpsellEvents")
          .then((mod) => mod.emitUpsellEvent({
            feature_key: detail.feature_key,
            addon_plan_id: detail.addon_plan_id,
            source_url: err?.config?.url,
          }))
          .catch(() => { /* best-effort */ });
      }
      return Promise.reject(err);
    }

    const isCreditWall =
      status === 429 &&
      typeof detail === "string" &&
      (detail.startsWith("llm_daily_cap_reached") || detail === "out_of_credits");
    if (isCreditWall && Date.now() - _lastCreditToastAt > 30_000) {
      _lastCreditToastAt = Date.now();
      // Defer require() at runtime: we don't want this lib module to import
      // sonner / react-router and risk a circular dep during bundling.
      Promise.all([import("sonner"), import("../contexts/CreditWallEvents")])
        .then(([{ toast }, mod]) => {
          mod.emitCreditWall({ detail });
          toast.error(
            detail === "out_of_credits"
              ? "Sin créditos de IA. Compra un pack para continuar."
              : "Has alcanzado el tope diario gratuito. Mejora o compra un pack.",
            {
              duration: 8000,
              action: {
                label: "Ver planes",
                onClick: () => { window.location.href = "/pricing"; },
              },
            },
          );
        })
        .catch(() => { /* swallow — toast is best-effort */ });
    }
    return Promise.reject(err);
  },
);

/* Known backend error codes → human-friendly Spanish/English messages.
 *
 * The backend returns short snake_case codes (e.g. `whatsapp_addon_required`)
 * to keep payloads cheap and lang-agnostic. The UI maps them here for
 * presentation. Falls back to the raw code when no translation exists,
 * so unknown codes still surface useful info instead of "Something went
 * wrong". Lang detection reads `localStorage.lumfi.lang` (set by the
 * LangContext) — Spanish is the default in our user base.
 */
const ERROR_MESSAGES = {
  es: {
    whatsapp_addon_required:
      "Tu plan no incluye WhatsApp. Pídele al equipo Lumfi que te asigne el addon \"+WhatsApp por línea\" o suscríbete desde Planes y packs.",
    telegram_addon_required:
      "Tu plan no incluye Telegram. Pídele al equipo Lumfi que te asigne el addon \"+Telegram\" o suscríbete desde Planes y packs.",
    advisor_chat_addon_required:
      "Tu plan no incluye el chat del asesor. Pídele al equipo Lumfi que te asigne el bono correspondiente o sube de plan.",
    feature_required:
      "Esta función no está incluida en tu plan actual. Revisa tu plan o pídele al equipo un bono.",
    plan_already_owned:
      "El usuario ya tiene este plan activo (pagado o por bono).",
    plan_not_superior:
      "El bono debe ser de un plan superior al que el usuario ya tiene.",
    plan_not_found:
      "Ese plan no existe o está deshabilitado.",
    auth_required:
      "Necesitas iniciar sesión para continuar.",
    forbidden:
      "No tienes permisos para hacer esta acción.",
    // iter-169zw — Turnstile expired vs invalid distinction. The
    // expired copy MUST imply auto-retry so the user doesn't think
    // they need to reload the page.
    captcha_expired:
      "La verificación caducó. Volvemos a validar y reintenta el ingreso en un instante…",
    captcha_failed:
      "La verificación de seguridad falló. Recarga la página si el problema persiste.",
  },
  en: {
    whatsapp_addon_required:
      "Your plan doesn't include WhatsApp. Ask the Lumfi team to grant you the \"+WhatsApp per line\" addon, or subscribe from Plans & packs.",
    telegram_addon_required:
      "Your plan doesn't include Telegram. Ask the Lumfi team to grant you the \"+Telegram\" addon, or subscribe from Plans & packs.",
    advisor_chat_addon_required:
      "Your plan doesn't include advisor chat. Ask the Lumfi team for a bonus or upgrade your plan.",
    feature_required:
      "This feature isn't included in your current plan. Review your plan or ask the team for a bonus.",
    plan_already_owned:
      "User already has this plan active (paid or via bonus).",
    plan_not_superior:
      "The bonus must be a higher tier than the user's current plan.",
    plan_not_found:
      "That plan doesn't exist or is disabled.",
    auth_required:
      "You need to sign in to continue.",
    forbidden:
      "You don't have permission for this action.",
    captcha_expired:
      "The security check expired. We're refreshing it and will retry in a moment…",
    captcha_failed:
      "The security check failed. Refresh the page if the problem persists.",
  },
};

function _getLang() {
  try {
    const v = localStorage.getItem("lumfi_lang");
    if (v === "en" || v === "pt") return v;
  } catch { /* ignore */ }
  return "es";
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    const lang = _getLang();
    const dict = ERROR_MESSAGES[lang] || ERROR_MESSAGES.es;
    if (dict[detail]) return dict[detail];
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatMoney(amount, currency = "USD") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat(currency === "COP" ? "es-CO" : "en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}
